import type { BusbarSelectionResponse } from "../services/busbarSelection";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import {
  BUSBAR_MATERIAL_LABELS,
  MATERIAL_CHOICE_LABELS,
} from "./busbarSelectionDraft";
import {
  arrangementText,
  checkCounts,
  STUDY_STATUS_LABELS,
  STUDY_STATUS_TONE,
} from "./busbarSelectionResultWords";
import { StatusIcon } from "./StatusIcon";
import { bannerTone } from "./statusTone";

type BusbarSelectionResultSummaryProps = {
  result: BusbarSelectionResponse;
};

/** "Busbar selection not met" for a failed check; the other statuses' own words. */
export function statusHeading(result: BusbarSelectionResponse): string {
  return STUDY_STATUS_LABELS[result.status];
}

// One line per material the study compared: the arrangement, or the catalogue row's rated
// current for a busway. A material with no solution shows an empty quantity.
function arrangementLines(result: BusbarSelectionResponse): string[] {
  if (result.system_kind === "BUSWAY") {
    return result.busway_selections.map(
      (selection) =>
        `${BUSBAR_MATERIAL_LABELS[selection.material]}: ${
          selection.row === null
            ? EMPTY_QUANTITY
            : formatQuantity(selection.row.rated_current_a, "A").display
        }`,
    );
  }
  if (result.evaluated_arrangement !== null) {
    return [arrangementText(result.evaluated_arrangement)];
  }
  return result.selections.map(
    (selection) =>
      `${BUSBAR_MATERIAL_LABELS[selection.material]}: ${
        selection.arrangement === null ? EMPTY_QUANTITY : arrangementText(selection.arrangement)
      }`,
  );
}

// The decisive values first (Master Prompt v2.1 section 19): the status, the material asked
// for, the arrangement chosen or evaluated, the design current against the capability the
// engine established, and how many checks stand at each status, a passed screening counted
// apart as "Preliminary pass". Nothing is calculated here: every value is the engine's, echoed
// or picked, and a value the engine did not establish shows an empty quantity. A review
// required or not applicable check is counted under its own word and never as a pass. Display
// rule A12: four significant figures, the exact decimal in the tooltip (title).
export function BusbarSelectionResultSummary({ result }: BusbarSelectionResultSummaryProps) {
  const tone = STUDY_STATUS_TONE[result.status];
  const design = formatQuantity(result.design_current_a, "A");
  const capacity = formatQuantity(result.effective_capacity_a, "A");
  const load = formatQuantity(result.load_current_a, "A");
  const margin = formatQuantity(result.margin_factor);
  const lines = arrangementLines(result);

  return (
    <section aria-label="Result summary" data-summary-tone={tone}>
      <header>
        <p data-summary-status={result.status}>
          <StatusIcon tone={bannerTone(result.status, STUDY_STATUS_TONE)} size={20} />
          {statusHeading(result)}
        </p>
        <p>
          Study <strong>{result.code}</strong> · {result.name} · {result.jurisdiction_profile}{" "}
          profile
        </p>
      </header>
      <dl data-summary-strip>
        <div>
          <dt>Material</dt>
          <dd data-summary-field="material" data-summary-value="text">
            {MATERIAL_CHOICE_LABELS[result.material]}
          </dd>
        </div>
        <div>
          <dt>Arrangement</dt>
          <dd data-summary-field="arrangement" data-summary-value="text">
            {lines.length === 0
              ? EMPTY_QUANTITY
              : lines.map((line, index) => (
                  <span key={line} data-arrangement-line="true">
                    {index > 0 ? " · " : null}
                    {line}
                  </span>
                ))}
          </dd>
        </div>
        <div>
          <dt>Design current</dt>
          {/* The design current against the capability the engine established. When it
              established none (a material not chosen, a busway), the tile says so in words
              instead of showing a dash the engineer has to interpret (follow-up 12). */}
          <dd
            data-summary-field="design-current"
            data-summary-value="text"
            title={[design.exact, capacity.exact].filter(Boolean).join(" · ") || undefined}
          >
            {capacity.exact === null
              ? `${design.display} — capability not established`
              : `${design.display} of ${capacity.display}`}
          </dd>
        </div>
        {result.load_current_a !== null && result.margin_factor !== null ? (
          <div>
            <dt>Design current basis</dt>
            {/* Id = Ib x Km, both as entered, when the design current was built from them. */}
            <dd
              data-summary-field="design-current-basis"
              data-summary-value="text"
              title={`${load.exact ?? ""} × ${margin.exact ?? ""}`}
            >
              Design current = load current × margin factor = {load.display} × {margin.display}
            </dd>
          </div>
        ) : null}
        <div>
          <dt>Checks</dt>
          <dd data-summary-field="checks" data-summary-value="text">
            {checkCounts(result.checks).map(({ key, word, count }, index) => (
              <span key={key} data-check-count={key}>
                {index > 0 ? " · " : null}
                {word} {count}
              </span>
            ))}
          </dd>
        </div>
        <div>
          <dt>Warnings</dt>
          <dd data-summary-warnings={result.warnings.length}>{result.warnings.length}</dd>
        </div>
      </dl>
    </section>
  );
}

export default BusbarSelectionResultSummary;
